import { useEffect } from 'react';

import { CollectionDetailsStep, CollectionPartnerSelectStep } from 'pages/partners/pages/collections/components/steps';
import { SelectablePartner, useCollections } from 'pages/partners/pages/collections/context';
import { CollectionApi, CollectionLoginUser } from 'apis/CollectionApi';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { PageHeader } from 'components/layout/page';
import { useNavigate } from 'react-router-dom';
import { Card } from 'react-bootstrap';
import { useSuperState } from 'hooks';

export const CollectionEdit = () => {

    const [state, setState] = useSuperState({
        name: '',
        collectionId: 0,
        description: '',
        existingLoginUsers: [] as CollectionLoginUser[],
        hasLogin: false,
        loading: true,
        saving: false,
        selectedPartners: [] as SelectablePartner[]
    });

    const { name, collectionId, description, existingLoginUsers, hasLogin, loading, saving, selectedPartners } = state;

    const { formatMessage } = useIntl();
    const { availablePartners, permissions } = useCollections();
    const { createToast } = useNotification();
    const navigate = useNavigate();

    useEffect(() => {

        const params = new URLSearchParams(window.location.search);
        const id = parseInt(params.get('collectionId') ?? '0', 10);

        if (id > 0) {

            setState({ collectionId: id });
            loadCollectionData(id);

        } else {

            setState({ loading: false });

        }

    }, []);

    const loadCollectionData = async (id: number) => {

        try {

            setState({ loading: true });

            const detail = await CollectionApi.Get(id);

            const collPartners: SelectablePartner[] = (detail.Partners ?? []).map(p => {

                const match = availablePartners.find(ap => ap.ID === p.Id);

                return {
                    Active: p.Active,
                    Country: { Id: 0, Name: p.Country ?? '' },
                    DateCreate: match?.DateCreate ?? p.DateCreate,
                    ID: p.Id,
                    Name: p.Name,
                    Sector: match?.Sector ?? ''
                };

            });

            setState({
                name: detail.Collection.Name,
                description: detail.Collection.Description ?? '',
                existingLoginUsers: detail.LoginUsers ?? [],
                hasLogin: detail.Collection.HasLogin,
                loading: false,
                selectedPartners: collPartners
            });

        } catch (e) {

            setState({ loading: false });

        }

    };

    const handleSave = async () => {

        if (!state.name.trim()) {

            createToast(
                formatMessage({ id: 'association_name_is_required.16af68f6', defaultMessage: 'Association name is required.' }),
                'danger'
            );
            return;

        }

        if (state.selectedPartners.length < 2) {

            createToast(
                formatMessage({ id: 'to_create_a_collection_at_least_2_testin.e198d319', defaultMessage: 'To create a collection, at least 2 Testing Centers must be added.' }),
                'danger'
            );
            return;

        }

        try {

            setState({ saving: true });
            const partnerIds = state.selectedPartners.map(p => p.ID).join(',');

            const response = await CollectionApi.Update(state.collectionId, state.name, state.description, partnerIds);

            if (response?.Done) {

                createToast(
                    formatMessage({ id: 'association_updated_successfully.2bb21b15', defaultMessage: 'Association updated successfully.' }),
                    'success'
                );

            } else {

                createToast(
                    response?.Message ?? 'Failed to update association.',
                    'danger'
                );

            }

        } catch (e) {

            createToast(
                formatMessage({ id: 'an_error_occurred_while_updating_the_ass.477fa68a', defaultMessage: 'An error occurred while updating the association.' }),
                'danger'
            );

        } finally {

            setState({ saving: false });

        }

    };

    return (
        <div className="collection-edit d-flex flex-column gap-3">
            <PageHeader
                cta={permissions.Edit ? {
                    disabled: loading || saving,
                    label: saving ? formatMessage({ id: 'saving.c916139e', defaultMessage: 'Saving...' }) : (formatMessage({ id: 'update_association.2d0c17c4', defaultMessage: 'Update Association' })),
                    onClick: handleSave
                } : undefined}
                current={name || formatMessage({ id: 'edit_association.8e91d200', defaultMessage: 'Edit Association' })}
                title={formatMessage({ id: 'edit_association.8e91d200', defaultMessage: 'Edit Association' })}
                trail={[{ label: formatMessage({ id: 'associations.a5026e86', defaultMessage: 'Associations' }), to: '/associations' }]}
            />

            { loading ?
                (
                    <div className={'d-flex align-items-center justify-content-center p-5'}>
                        <GMetrixLoader size={'large'}/>
                    </div>
                )
                :
                (
                    <>
                        {collectionId === 0 ?
                            (
                                <>
                                    <div className="alert alert-warning">

                                        <FormattedMessage defaultMessage="No collection selected. Please return to the list." id="no_collection_selected_please_return_to.78e0ce7d" />
                                    </div>
                                    <GMetrixButton
                                        onClick={() => navigate('/collections')}
                                        variant="primary"
                                    >
                                        {<FormattedMessage defaultMessage="Return to Custom Tests" id="return_to_custom_tests.f596c5cb" />}
                                    </GMetrixButton>
                                </>
                            )
                            :
                            (
                                <>

                                    <Card body className="mb-4">
                                        <CollectionDetailsStep
                                            isEditMode
                                            createLogin={false}
                                            description={description}
                                            email=""
                                            existingLoginUsers={existingLoginUsers}
                                            firstName=""
                                            hasLogin={hasLogin}
                                            lastName=""
                                            name={name}
                                            onDescriptionChange={value => setState({ description: value })}
                                            onNameChange={value => setState({ name: value })}
                                        />
                                    </Card>

                                    <Card body>
                                        <CollectionPartnerSelectStep
                                            onSelectedPartnersChange={value => setState({ selectedPartners: value })}
                                            selectedPartners={selectedPartners}
                                        />
                                    </Card>
                                </>
                            )
                        }
                    </>
                )
            }

        </div>
    );

};
